import React, { useState, useEffect, useMemo, useContext } from 'react';
import { AuthContext } from '../../App';
import { useNavigate } from 'react-router-dom';
import { dataService } from '../../services/dataService';
import { StudentGroup, LearningPath } from '../../data/types';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import PageContainer from '../layouts/PageContainer';

const StudentGroupsScreen: React.FC = () => {
  const { activeRole } = useContext(AuthContext);
  const [groups, setGroups] = useState<StudentGroup[]>([]);
  const [learningPaths, setLearningPaths] = useState<LearningPath[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);

  // Reset state when role changes
  useEffect(() => {
    setSearchQuery('');
    setShowInactive(false);
  }, [activeRole]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [groupsData, pathsData] = await Promise.all([
          dataService.getStudentGroups(),
          dataService.getLearningPaths()
        ]);
        setGroups(groupsData);
        setLearningPaths(pathsData);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const filteredGroups = useMemo(() => {
    return groups
      .filter(group => 
        group.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
        (showInactive || group.status === 'active')
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [groups, searchQuery, showInactive]);

  const handleCreateGroup = () => {
    navigate('/student-groups/create');
  };

  const handleEditGroup = (groupId: string) => {
    navigate(`/student-groups/${groupId}/edit`);
  };

  const handleViewGroup = (groupId: string) => {
    navigate(`/student-groups/${groupId}`);
  };

  const handleDeleteGroup = async (groupId: string) => {
    if (window.confirm('Are you sure you want to delete this group?')) {
      try {
        await dataService.deleteStudentGroup(groupId);
        setGroups(groups.filter(group => group.id !== groupId));
      } catch (error) {
        console.error('Error deleting group:', error);
      }
    }
  };

  const handleToggleStatus = async (groupId: string) => {
    try {
      await dataService.toggleGroupStatus(groupId);
      // Update the local state after toggling
      setGroups(prevGroups => 
        prevGroups.map(group => 
          group.id === groupId 
            ? { ...group, status: group.status === 'active' ? 'inactive' : 'active' }
            : group
        )
      );
    } catch (error) {
      console.error('Error toggling group status:', error);
    }
  };

  const getLearningPathName = (pathId: string) => {
    const path = learningPaths.find(p => p.id === pathId);
    return path ? path.name : 'Not Assigned';
  };

  const getGroupProgress = (group: StudentGroup) => {
    // For mock purposes, calculate a random progress percentage
    // In a real app, this would be calculated based on student progress in the group
    return Math.floor(Math.random() * 100);
  };

  return (
    <PageContainer title="Student Groups" userRole="Teacher">
      <div className="flex justify-end mb-4">
        <Button variant="primary" onClick={handleCreateGroup}>
          Create Group
        </Button>
      </div>

      <Card>
        <div className="p-4">
          <div className="flex justify-between items-center mb-4">
            <div className="flex-1 max-w-md">
              <input
                type="text"
                placeholder="Search groups..."
                className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="flex items-center">
              <label className="inline-flex items-center">
                <input
                  type="checkbox"
                  className="form-checkbox h-5 w-5 text-primary"
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                />
                <span className="ml-2 text-gray-700">Show Inactive</span>
              </label>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center items-center h-40">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <>
              {filteredGroups.length === 0 ? (
                <div className="text-center py-10">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No groups found</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {searchQuery ? 'Try adjusting your search terms.' : 'Get started by creating a new group.'}
                  </p>
                  {!searchQuery && (
                    <div className="mt-6">
                      <Button variant="primary" onClick={handleCreateGroup}>
                        Create New Group
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Group ID
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Group Name
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Current Learning Path
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Progress
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredGroups.map(group => {
                        const progress = getGroupProgress(group);
                        // Get all learning paths for the group
                        const learningPathsDisplay = group.learningPaths && group.learningPaths.length > 0
                          ? group.learningPaths.map(pathId => getLearningPathName(pathId))
                          : ['Not Assigned'];
                        
                        return (
                          <tr key={group.id} className={group.status === 'inactive' ? 'bg-gray-50' : ''}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {group.id}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">{group.name}</div>
                              <div className="text-sm text-gray-500 truncate max-w-xs">{group.description}</div>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500">
                              <div className="flex flex-col space-y-1">
                                {learningPathsDisplay.map((pathName, index) => (
                                  <div key={index} className="truncate max-w-xs" title={pathName}>
                                    {pathName}
                                  </div>
                                ))}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="w-full bg-gray-200 rounded-full h-2.5">
                                <div 
                                  className="bg-primary h-2.5 rounded-full" 
                                  style={{ width: `${progress}%` }}
                                ></div>
                              </div>
                              <div className="text-xs mt-1 text-gray-500">{progress}% Complete</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <Badge 
                                  variant={group.status === 'active' ? 'success' : 'error'}
                                  size="sm"
                                >
                                  {group.status === 'active' ? 'Active' : 'Inactive'}
                                </Badge>
                                <button 
                                  onClick={() => handleToggleStatus(group.id)}
                                  className="ml-2 text-sm text-gray-500 hover:text-gray-700"
                                >
                                  {group.status === 'active' ? 'Deactivate' : 'Activate'}
                                </button>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <div className="flex space-x-2">
                                <Button 
                                  variant="tertiary" 
                                  size="sm" 
                                  onClick={() => handleViewGroup(group.id)}
                                >
                                  View
                                </Button>
                                <Button 
                                  variant="tertiary" 
                                  size="sm" 
                                  onClick={() => handleEditGroup(group.id)}
                                >
                                  Edit
                                </Button>
                                <Button 
                                  variant="tertiary" 
                                  size="sm" 
                                  className="text-red-600 hover:text-red-800"
                                  onClick={() => handleDeleteGroup(group.id)}
                                >
                                  Delete
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </Card>
    </PageContainer>
  );
};

export default StudentGroupsScreen;
